import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PublicLayout } from "@/components/public/PublicLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/public/DateField";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Lock, Loader2, Check, Sparkles, PartyPopper, Info, MessageCircle, Smartphone } from "lucide-react";

function ComboField({ id, value, onChange, suggestions, placeholder }) {
  const [open, setOpen] = useState(false);
  const q = value || "";
  const trimmed = q.trim().toLowerCase();
  const matches = trimmed.length >= 3
    ? suggestions.filter((s) => s.toLowerCase().includes(trimmed)).slice(0, 8)
    : [];
  return (
    <div className="relative">
      <Input
        data-testid={`field-${id}`}
        value={q}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder={placeholder}
        className="h-12 rounded-xl"
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <ul className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-auto" data-testid={`field-${id}-suggestions`}>
          {matches.map((s) => (
            <li key={s}>
              <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { onChange(s); setOpen(false); }}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-indigo-50 text-slate-700">
                {s}
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && trimmed.length > 0 && trimmed.length < 3 && (
        <p className="mt-1.5 text-xs text-slate-400">Tapez au moins 3 lettres…</p>
      )}
    </div>
  );
}

function Field({ field, value, onChange }) {
  const id = field.name;
  if (field.type === "combo") {
    return <ComboField id={id} value={value} onChange={onChange} suggestions={field.suggestions || []} placeholder={field.placeholder || "Tapez au moins 3 lettres…"} />;
  }
  if (field.type === "radio") {
    return (
      <RadioGroup value={value || ""} onValueChange={onChange} className="grid sm:grid-cols-2 gap-3" data-testid={`field-${id}`}>
        {field.options.map((o) => {
          const active = value === o.value;
          return (
            <button type="button" key={o.value} onClick={() => onChange(o.value)}
              className={`relative flex items-center gap-3 border-2 rounded-2xl p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-indigo-500 bg-indigo-50/70 shadow-md shadow-indigo-500/10" : "border-slate-200 hover:border-indigo-300"}`}>
              <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${active ? "border-indigo-500 bg-indigo-500" : "border-slate-300"}`}>{active && <Check size={13} className="text-white animate-pop" />}</span>
              <span className="text-sm font-semibold text-slate-800">{o.label}</span>
            </button>
          );
        })}
      </RadioGroup>
    );
  }
  if (field.type === "checkboxes") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="grid sm:grid-cols-2 gap-3" data-testid={`field-${id}`}>
        {field.options.map((o) => {
          const checked = arr.includes(o.value);
          return (
            <button type="button" key={o.value} onClick={() => onChange(checked ? arr.filter((x) => x !== o.value) : [...arr, o.value])}
              className={`flex items-center gap-3 border-2 rounded-2xl p-4 text-left transition-[border-color,background-color,transform] duration-200 hover:-translate-y-0.5 ${checked ? "border-[#9B86B5] bg-[#F3F1F9] shadow-md shadow-[#9B86B5]/10" : "border-slate-200 hover:border-[#C0B2D4]"}`}>
              <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 ${checked ? "border-[#9B86B5] bg-[#9B86B5]" : "border-slate-300"}`}>{checked && <Check size={13} className="text-white animate-pop" />}</span>
              <span className="text-sm font-semibold text-slate-800">{o.label}</span>
            </button>
          );
        })}
      </div>
    );
  }
  if (field.type === "select") {
    return (
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger data-testid={`field-${id}`} className="h-12 rounded-xl"><SelectValue placeholder={field.placeholder || "Sélectionnez"} /></SelectTrigger>
        <SelectContent>{field.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    );
  }
  if (field.type === "slider") {
    const v = typeof value === "number" ? value : field.default ?? field.min;
    return (
      <div data-testid={`field-${id}`} className="pt-2">
        <div className="flex items-end justify-center mb-4">
          <span className="font-display text-4xl font-extrabold text-gradient-warm">{v}</span><span className="text-lg font-bold text-slate-400 mb-1">{field.unit}/mois</span>
        </div>
        <Slider min={field.min} max={field.max} step={field.step || 1} value={[v]} onValueChange={(a) => onChange(a[0])} />
        <div className="flex justify-between text-xs text-slate-400 mt-2"><span>{field.min}{field.unit}</span><span>{field.max}{field.unit}</span></div>
      </div>
    );
  }
  if (field.type === "textarea") return <Textarea data-testid={`field-${id}`} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} />;
  if (field.type === "date") {
    return <DateField value={value} onChange={onChange} birth={id.includes("naissance")} testId={`field-${id}`} placeholder={field.placeholder || "Choisir une date"} />;
  }
  if (field.type === "needs_grid") {
    const val = value && typeof value === "object" ? value : {};
    const LEVELS = [["minimum", "Minimum"], ["moyen", "Moyen"], ["fort", "Fort"], ["maximum", "Maximum"]];
    const set = (k, lv) => onChange({ ...val, [k]: lv });
    return (
      <TooltipProvider delayDuration={100}>
        <div className="space-y-5" data-testid={`field-${id}`}>
          {field.posts.map((p) => (
            <div key={p.key} className="border border-slate-100 rounded-2xl p-4 sm:p-5 bg-white shadow-sm">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0"><p.icon className="text-blue-700" size={20} /></div>
                <div className="flex-1 min-w-0">
                  <span className="font-bold text-slate-900">{p.label}</span>
                  {p.desc && <span className="text-xs text-slate-400 italic ml-2">{p.desc}</span>}
                </div>
                {p.info && (
                  <Tooltip>
                    <TooltipTrigger asChild><button type="button" aria-label="Plus d'infos" className="text-slate-300 hover:text-blue-600 shrink-0"><Info size={17} /></button></TooltipTrigger>
                    <TooltipContent className="max-w-[240px] text-sm">{p.info}</TooltipContent>
                  </Tooltip>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                {LEVELS.map(([lv, label]) => {
                  const active = val[p.key] === lv;
                  return (
                    <button type="button" key={lv} onClick={() => set(p.key, lv)} data-testid={`${id}-${p.key}-${lv}`}
                      className={`rounded-xl py-3 text-sm font-semibold border-2 transition-[border-color,background-color,color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-transparent bg-gradient-to-br from-indigo-600 to-indigo-500 text-white shadow-md shadow-indigo-500/20" : "border-slate-200 text-slate-600 hover:border-indigo-300"}`}>
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </TooltipProvider>
    );
  }
  if (field.type === "level") {
    const labels = ["Minimal", "Intermédiaire", "Confort", "Maximal"];
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid={`field-${id}`}>
        {labels.map((lvl, idx) => {
          const active = value === idx + 1;
          return (
            <button type="button" key={lvl} onClick={() => onChange(idx + 1)}
              className={`border-2 rounded-2xl py-4 px-2 text-sm font-bold transition-[border-color,background-color,color,transform] duration-200 hover:-translate-y-0.5 ${active ? "border-transparent bg-gradient-to-br from-indigo-600 to-indigo-500 text-white shadow-lg shadow-indigo-500/25" : "border-slate-200 text-slate-700 hover:border-indigo-300"}`}>
              <span className="block text-xs opacity-60 mb-0.5">Niveau {idx + 1}</span>{lvl}
            </button>
          );
        })}
      </div>
    );
  }
  return <Input data-testid={`field-${id}`} type={field.type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className="h-12 rounded-xl" />;
}

export function SimulatorWizard({ simType, title, subtitle, steps }) {
  const nav = useNavigate();
  const [stepIdx, setStepIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [contact, setContact] = useState({ email: "", first_name: "", last_name: "", phone: "", contact_preference: "telephone", channel: "sms", consent: false });
  const [submitting, setSubmitting] = useState(false);

  const accent = "indigo";
  const accentText = "text-indigo-600";
  const totalSteps = steps.length + 1;
  const isContact = stepIdx === steps.length;
  const progress = Math.round(((stepIdx + 1) / totalSteps) * 100);
  const encourage = ["C'est parti ! 🎯", "Super, continuez !", "Presque fini !", "Dernière étape 🎉"];

  const setAnswer = (name, val) => setAnswers((a) => ({ ...a, [name]: val }));

  const validateStep = () => {
    if (isContact) {
      const digits = (contact.phone || "").replace(/\D/g, "");
      if (digits.length < 8) { toast.error("Veuillez saisir un numéro de téléphone portable valide."); return false; }
      if (!contact.consent) { toast.error("Veuillez accepter le traitement de vos données pour continuer."); return false; }
      return true;
    }
    for (const f of steps[stepIdx].fields) {
      if (f.type === "needs_grid" && f.required) {
        const v = answers[f.name] && typeof answers[f.name] === "object" ? answers[f.name] : {};
        if (!f.posts.every((p) => v[p.key])) { toast.error("Veuillez évaluer chaque poste de santé."); return false; }
        continue;
      }
      if (f.required && (answers[f.name] === undefined || answers[f.name] === "" || (Array.isArray(answers[f.name]) && !answers[f.name].length))) {
        toast.error(`Veuillez compléter : ${f.label}`); return false;
      }
    }
    return true;
  };

  const next = async () => {
    if (!validateStep()) return;
    if (!isContact) { setStepIdx((i) => i + 1); window.scrollTo({ top: 0, behavior: "smooth" }); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/simulations", {
        type: simType, answers, email: contact.email?.trim() || undefined, first_name: contact.first_name, last_name: contact.last_name,
        phone: contact.phone, contact_preference: contact.contact_preference, channel: contact.channel, source: "site_web", consent_processing: true,
      });
      const dev = data.delivery || {};
      nav("/verification", { state: { prospect_id: data.prospect_id, phone: contact.phone, channel: dev.channel || contact.channel, dev_code: dev.dev_code, delivered: dev.delivered, delivery_error: dev.error } });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <PublicLayout>
      <div className="relative" style={{ background: "linear-gradient(180deg,#F8FAFC,#EEF4FF 55%,#ffffff)" }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
          {/* progress header */}
          <div className="mb-8">
            <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accentText}`}>{title}</p>
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-slate-900 mt-1">{subtitle}</h1>
            <div className="flex items-center gap-4 mt-5">
              <div className="flex-1 h-2.5 rounded-full bg-slate-200 overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${progress}%`, background: "linear-gradient(to right,#343A86,#7E82C4)" }} />
              </div>
              <span className="text-sm font-bold text-slate-500 shrink-0 tabular-nums">{progress}%</span>
            </div>
            <div className="flex items-center gap-2 mt-3">
              {Array.from({ length: totalSteps }).map((_, i) => (
                <div key={i} className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors ${i < stepIdx ? "bg-[#8E7FB0] text-white" : i === stepIdx ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-400"}`}>
                  {i < stepIdx ? <Check size={15} /> : i + 1}
                </div>
              ))}
              <span className="ml-2 text-sm font-semibold text-slate-600 inline-flex items-center gap-1"><Sparkles size={15} className="text-indigo-500" />{encourage[Math.min(stepIdx, encourage.length - 1)]}</span>
            </div>
          </div>

          <Card className="p-6 sm:p-8 rounded-3xl shadow-xl border-slate-100 animate-fade-up" data-testid={`step-${stepIdx + 1}-form`}>
            {!isContact ? (
              <>
                <h2 className="font-display text-xl font-bold text-slate-900">{steps[stepIdx].title}</h2>
                {steps[stepIdx].description && <p className="text-sm text-slate-500 mt-1">{steps[stepIdx].description}</p>}
                <div className="space-y-7 mt-6">
                  {steps[stepIdx].fields.map((f) => (
                    <div key={f.name}>
                      <Label className="text-sm font-bold text-slate-800 mb-3 block">{f.label}{f.required && <span className="text-indigo-500"> *</span>}</Label>
                      <Field field={f} value={answers[f.name]} onChange={(v) => setAnswer(f.name, v)} />
                      {f.hint && <p className="text-xs text-slate-400 mt-2">{f.hint}</p>}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2"><PartyPopper className="text-indigo-500" size={22} /><h2 className="font-display text-xl font-bold text-slate-900">Dernière étape : vos coordonnées</h2></div>
                <div className="flex items-start gap-2 text-sm text-blue-900 bg-blue-50 rounded-xl p-4 mt-4 border border-blue-100">
                  <Lock size={16} className="mt-0.5 shrink-0 text-blue-600" />
                  <span>Pour accéder à votre comparatif personnalisé, nous vérifions votre numéro de téléphone par SMS. Cette vérification ne constitue pas un consentement à recevoir des publicités.</span>
                </div>
                <div className="space-y-5 mt-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div><Label className="mb-2 block font-semibold">Prénom</Label><Input data-testid="contact-firstname" value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} className="h-12 rounded-xl" /></div>
                    <div><Label className="mb-2 block font-semibold">Nom</Label><Input data-testid="contact-lastname" value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} className="h-12 rounded-xl" /></div>
                  </div>
                  <div><Label className="mb-2 block font-semibold">Téléphone portable <span className="text-indigo-500">*</span></Label><Input data-testid="contact-phone" type="tel" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} placeholder="06 12 34 56 78" className="h-12 rounded-xl" /></div>
                  <div>
                    <Label className="mb-2 block font-semibold">Recevoir mon code par</Label>
                    <div className="grid grid-cols-2 gap-3">
                      <button type="button" data-testid="channel-sms" onClick={() => setContact({ ...contact, channel: "sms" })} className={`h-12 rounded-xl border-2 font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${contact.channel === "sms" ? "border-indigo-500 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:border-indigo-300"}`}><Smartphone size={16} />SMS</button>
                      <button type="button" data-testid="channel-whatsapp" onClick={() => setContact({ ...contact, channel: "whatsapp" })} className={`h-12 rounded-xl border-2 font-semibold text-sm flex items-center justify-center gap-2 transition-colors ${contact.channel === "whatsapp" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-600 hover:border-emerald-300"}`}><MessageCircle size={16} />WhatsApp</button>
                    </div>
                    <p className="text-xs text-slate-400 mt-2">Par défaut par SMS. WhatsApp bientôt disponible.</p>
                  </div>
                  <div><Label className="mb-2 block font-semibold">Adresse e-mail <span className="text-slate-400 text-xs font-normal">(facultatif)</span></Label><Input data-testid="contact-email" type="email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} placeholder="vous@exemple.fr" className="h-12 rounded-xl" /></div>
                  <div>
                    <Label className="mb-2 block font-semibold">Préférence de contact</Label>
                    <Select value={contact.contact_preference} onValueChange={(v) => setContact({ ...contact, contact_preference: v })}>
                      <SelectTrigger data-testid="contact-preference" className="h-12 rounded-xl"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="email">E-mail</SelectItem><SelectItem value="telephone">Téléphone</SelectItem><SelectItem value="indifferent">Indifférent</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <label className="flex items-start gap-3 cursor-pointer bg-slate-50 rounded-xl p-4 border border-slate-100">
                    <Checkbox data-testid="contact-consent" checked={contact.consent} onCheckedChange={(c) => setContact({ ...contact, consent: !!c })} className="mt-0.5" />
                    <span className="text-sm text-slate-600">J'accepte que mes informations soient utilisées pour traiter ma demande de comparaison et être recontacté(e) par un conseiller.</span>
                  </label>
                </div>
              </>
            )}

            <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-100">
              <Button variant="ghost" onClick={() => setStepIdx((i) => Math.max(0, i - 1))} disabled={stepIdx === 0} data-testid="wizard-back"><ArrowLeft size={16} className="mr-1" /> Retour</Button>
              <Button onClick={next} disabled={submitting} data-testid="wizard-next" className="btn-gradient border-0 rounded-full h-12 px-7 text-base">
                {submitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {isContact ? "Voir mon comparatif" : "Continuer"}
                {!isContact && !submitting && <ArrowRight size={16} className="ml-1.5" />}
              </Button>
            </div>
          </Card>
          <p className="text-center text-xs text-slate-400 mt-5 flex items-center justify-center gap-1.5"><Lock size={12} /> Données chiffrées · Sans engagement · Gratuit</p>
        </div>
      </div>
    </PublicLayout>
  );
}
